'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/auth';
import { ProfileDrawer } from '@/features/profile-drawer';
import { AccountIcon, IconState } from '@/shared-ui';
import { cn } from '@/lib/utils';
import { USER_ACTION_ITEM_CLASSES } from './item-classes';

/**
 * Account entry point — always visible. Logged out: animated notification dot + "My Account", opens the
 * {@link ProfileDrawer} sign-in content. Registered: greets by first name (from the id_token, server-side),
 * no dot, opens the authenticated slide-in. The label is desktop-only.
 */
export const AccountMenu = ({ className }: { className?: string }): ReactElement => {
    const session = useAuth();
    const { t } = useTranslation('header');

    const isRecognised = Boolean(session?.userType === 'registered' && session?.customerId);
    const firstName = session?.firstName;

    const label =
        isRecognised && firstName ? t('userActions.greeting', { name: firstName }) : t('userActions.myAccount');
    const ariaLabel = isRecognised ? t('userActions.myAccount') : t('signIn');

    const trigger = (
        <button type="button" className={cn(USER_ACTION_ITEM_CLASSES, className)} aria-label={ariaLabel}>
            <IconState
                visible={!isRecognised}
                color="bg-snk-fresh-pink"
                position="bottom-right"
                indicatorClassName="animate-[snkDotJump_2s_2_forwards] motion-reduce:animate-none">
                <AccountIcon className="size-6" />
            </IconState>
            <span className="hidden lg:inline">{label}</span>
        </button>
    );

    return <ProfileDrawer trigger={trigger} isAuthenticated={isRecognised} />;
};
